import React, { useEffect, ReactElement, ReactNode, PropsWithChildren } from 'react'
import { navigate } from 'gatsby'
import { Flex, Spinner, Text, ThemeUIStyleObject } from 'theme-ui'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { TransactionStatus, PaymentMethod, resetPaymentsState } from 'store/billing'
import PaymentMethodButtonGroup from 'components/Plans/Payments/PaymentMethodButtonGroup'
import platform from 'platform'
import usePaymentRequest from 'components/Plans/Payments/XPay/usePaymentRequest'
import { PlansSteps } from 'components/Plans/usePlanWizardSteps'
import useStripeElements from './Stripe/useStripeElements'
import { Setter } from 'utils'
import CreditCardInputs from './Stripe/CreditCardInputs'
import { Svg } from 'ui'
import StripeIcon from 'images/plans/stripe.svg'

const fillOutAccountInfoMessage = 'Please fill out your account information to enable this option.'
const loginOrAddPaymentMethodMessage =
  'Please login to your Google Chrome account or add a payment method to your Chrome browser to enable this option. Refresh this page once completed.'
const redirectionMessage = 'You will be redirected to a third-party to complete this purchase.'
const stripeMessage =
  'Payments are processed by Stripe. Control D doesn’t see or store your card information.'

export const RenderProcessing = (): ReactElement => {
  return (
    <Flex sx={{ alignItems: 'center', gap: '0.6rem' }}>
      <Spinner
        color="greenApple"
        sx={{
          width: '2rem',
          height: '2rem',
        }}
      />
      <Text variant="size15Weight600Line138" sx={{ color: 'aliceBlue' }}>
        Payment processing...
      </Text>
    </Flex>
  )
}

export default function PaymentForm({
  fullName,
  setFullName,
  setAreCreditCardInputsValid,
}: {
  fullName: string
  setFullName: (string) => void
  setAreCreditCardInputsValid: Setter<boolean>
}): ReactElement {
  const dispatch = useAppDispatch()
  const { transactionStatus, paymentRequest, paymentMethod, paymentUrl } = useAppSelector(
    s => s.payments,
  )
  const session = useAppSelector(s => s.session)

  usePaymentRequest()

  const { areCreditCardInputsValid } = useStripeElements()

  useEffect(() => {
    setAreCreditCardInputsValid(areCreditCardInputsValid)
  }, [areCreditCardInputsValid, setAreCreditCardInputsValid])

  useEffect(() => {
    return () => {
      dispatch(resetPaymentsState())
    }
  }, [dispatch])

  const isChrome = platform.name?.includes('Chrome')

  useEffect(() => {
    if (transactionStatus === TransactionStatus.SUCCESS) {
      navigate(`/plans?step=${PlansSteps.SUCCESS}`)
    } else if (paymentUrl) {
      // from crypto payment
      navigate(paymentUrl)
    }
  }, [paymentUrl, transactionStatus])

  const isCryptoPayment = paymentMethod === PaymentMethod.CRYPTO
  const isCreditPayment = paymentMethod === PaymentMethod.CREDIT
  const isPaymentRequest = paymentMethod !== PaymentMethod.CREDIT && !isCryptoPayment
  const getFillOutAccountInfoMessage = (): boolean | string =>
    !!session?.data?.email || (!!paymentRequest && isPaymentRequest && fillOutAccountInfoMessage)

  const getLoginOrAddPaymentMethodMessage = (): boolean | string =>
    transactionStatus !== TransactionStatus.INPROGRESS &&
    !paymentRequest &&
    isChrome &&
    !isCryptoPayment &&
    loginOrAddPaymentMethodMessage

  const getRedirectionMessage = (): boolean | string =>
    (isCryptoPayment ||
      (transactionStatus !== TransactionStatus.INPROGRESS &&
        !!paymentRequest &&
        !!session?.data?.email)) &&
    redirectionMessage

  const getStripeContent = (): boolean | ReactNode =>
    (isCreditPayment ||
      (transactionStatus !== TransactionStatus.INPROGRESS &&
        !!paymentRequest &&
        !!session?.data?.email)) && (
      <MessageWrapper>
        <Svg svg={StripeIcon} descriptiveText="Stripe logo" />
        <Text variant="size15Weight600Line138" sx={{ color: 'aliceBlue' }}>
          {stripeMessage}
        </Text>
      </MessageWrapper>
    )

  return (
    <Flex
      sx={{ flexDirection: 'column', width: '100%', gap: '1.6rem', textAlign: ['center', 'left'] }}
    >
      <Text variant="size21Weight600Line138" sx={{ color: 'aliceBlue' }} tabIndex={0}>
        Payment Details
      </Text>

      <Flex
        id="controld-payment-form"
        data-testid="controld-payment-form"
        className="card"
        sx={{
          width: '100%',
          flexDirection: 'column',
          alignItems: ['center', 'flex-start', 'center'],
        }}
      >
        <PaymentMethodButtonGroup />
        {getStripeContent()}
        {paymentMethod === PaymentMethod.CREDIT ? (
          <Flex
            sx={{
              width: '100%',
              flexDirection: 'column',
              mb: '4.8rem',
              mt: '2.4rem',
            }}
          >
            <CreditCardInputs fullName={fullName} setFullName={setFullName} />
          </Flex>
        ) : (
          <MessageWrapper sx={{ pb: '2.4rem' }}>
            <Text
              variant="size15Weight600Line138"
              sx={{
                pr: 0,
                textAlign: 'center',
                color: 'aliceBlue',
              }}
            >
              {getFillOutAccountInfoMessage()}
              {getLoginOrAddPaymentMethodMessage()}
              {getRedirectionMessage()}
            </Text>
          </MessageWrapper>
        )}
      </Flex>
    </Flex>
  )
}

function MessageWrapper({ children, sx }: PropsWithChildren<{ sx?: ThemeUIStyleObject }>) {
  return (
    <Flex
      sx={{
        width: '100%',
        maxWidth: '38.8rem',
        flexDirection: 'column',
        gap: '1.2rem',
        pt: '4.8rem',
        alignItems: 'center',
        textAlign: 'center',
        ...sx,
      }}
    >
      {children}
    </Flex>
  )
}
